import { useMemo } from 'react';

import { useNodesQuery } from '@cast/split-me/hooks/queries/nodes';
import { useListAllRebalancingConfigurationNodesQuery } from '@cast/split-me/hooks/queries/rebalancing-config';
import {
  getNodeAggressiveModeRequirement,
  isRequiredAggressiveModeConfigCovered,
  useIsKentRebalancingMode,
} from '@cast/split-me/features/cluster/rebalance';
import { AggressiveModeConfig, Node } from '@cast/types';
import type {
  KentGetRebalancingConstraintsRequest,
  KentRebalancingConstraints,
} from '@cast/types-kent';
import type { RebalancingConfigurationNode } from '@cast/types-cluster-autoscaler';
import { makeRefetchOnlyFailed, notReachable } from '@cast/utils';

import {
  OPTIMIZATION_GOAL_TO_GENERATE_AUTOSCALER_REBALANCER_PLAN_PARAMS,
  OPTIMIZATION_GOAL_TO_GENERATE_KENT_REBALANCER_PLAN_PARAMS,
} from '../../_constants';

import { OptimizationGoal } from '../../open-api-types/autopilot-types';
import { useGetKentRebalancingConstraintsQuery } from '@cast/split-me/hooks/queries/kent';

type EligibleRebalancingNode = {
  nodeName: string;
  castNodeId: string;
};

export type NodesEligibilityCheckResult =
  | { status: 'checking' }
  | { status: 'failed'; error: unknown; retry: () => void }
  | { status: 'no-eligible-nodes' }
  | { status: 'ready'; nodes: EligibleRebalancingNode[] };

const buildKentConstraintsRequest = ({
  clusterId,
  optimizationGoal,
}: {
  clusterId: string;
  optimizationGoal?: OptimizationGoal;
}): Omit<KentGetRebalancingConstraintsRequest, 'organizationId'> => {
  if (!optimizationGoal) {
    return { clusterId };
  }

  const params =
    OPTIMIZATION_GOAL_TO_GENERATE_KENT_REBALANCER_PLAN_PARAMS[optimizationGoal];

  return {
    clusterId,
    aggressiveModeConfigIgnoreLocalPersistentVolumes:
      params.kentAggressiveModeConfig?.ignoreLocalPersistentVolumes,
    aggressiveModeConfigIgnoreProblemJobPods:
      params.kentAggressiveModeConfig?.ignoreProblemJobPods,
    aggressiveModeConfigIgnoreProblemRemovalDisabledPods:
      params.kentAggressiveModeConfig?.ignoreProblemRemovalDisabledPods,
    aggressiveModeConfigIgnoreProblemPodsWithoutController:
      params.kentAggressiveModeConfig?.ignoreProblemPodsWithoutController,
    aggressiveModeConfigAggressiveEviction:
      params.kentAggressiveModeConfig?.aggressiveEviction,
    aggressiveModeConfigIgnoreInstanceCriteria:
      params.kentAggressiveModeConfig?.ignoreInstanceCriteria,
    aggressiveModeConfigIgnoreBlockingPdb:
      params.kentAggressiveModeConfig?.ignoreBlockingPdb,
    aggressiveModeConfigIgnoreZeroNodePoolDisruptionBudgets:
      params.kentAggressiveModeConfig?.ignoreZeroNodePoolDisruptionBudgets,
    aggressiveModeConfigIgnoreProblemRemovalDisabledNodes:
      params.kentAggressiveModeConfig?.ignoreProblemRemovalDisabledNodes,
    aggressiveModeConfigIgnoreConsolidatable:
      params.kentAggressiveModeConfig?.ignoreConsolidatable,
    aggressiveModeConfigDrainTimeoutSeconds:
      params.kentAggressiveModeConfig?.drainTimeoutSeconds,
  };
};

const getKentEligibleNodes = ({
  nodes,
  kentRebalancingConstraints,
}: {
  nodes: Node[];
  kentRebalancingConstraints: KentRebalancingConstraints | undefined;
}): EligibleRebalancingNode[] => {
  const problematicNodeNames = new Set(
    (kentRebalancingConstraints?.problematicNodes ?? []).map(
      (problematicNode) => problematicNode.nodeName
    )
  );

  return nodes
    .filter((node) => !problematicNodeNames.has(node.name))
    .map((node) => ({ nodeName: node.name, castNodeId: node.id }));
};

const getAutoscalerEligibleNodes = ({
  rebalancingConfigurationNodes,
  aggressiveMode,
  aggressiveModeConfig,
}: {
  rebalancingConfigurationNodes: RebalancingConfigurationNode[];
  aggressiveMode: boolean;
  aggressiveModeConfig: AggressiveModeConfig | undefined;
}): EligibleRebalancingNode[] =>
  rebalancingConfigurationNodes
    .filter((node) => {
      const nodeAggressiveModeRequirement =
        getNodeAggressiveModeRequirement(node);
      switch (nodeAggressiveModeRequirement.type) {
        case 'not_needed':
          return true;
        case 'incompatible':
          return false;
        case 'required': {
          if (!aggressiveMode || !aggressiveModeConfig) {
            return false;
          }
          return isRequiredAggressiveModeConfigCovered({
            required: nodeAggressiveModeRequirement.config,
            provided: aggressiveModeConfig,
          });
        }
        default:
          return notReachable(nodeAggressiveModeRequirement);
      }
    })
    .map((node) => ({
      nodeName: node.id?.name || '',
      castNodeId: node.id?.castNodeId || '',
    }));

const mapStateToEligibilityResult = ({
  isLoading,
  error,
  eligibleNodes,
  retry,
}: {
  isLoading: boolean;
  error: unknown;
  eligibleNodes: EligibleRebalancingNode[];
  retry: () => void;
}): NodesEligibilityCheckResult => {
  if (isLoading) {
    return { status: 'checking' };
  }
  if (error) {
    return { status: 'failed', error, retry };
  }
  if (eligibleNodes.length === 0) {
    return { status: 'no-eligible-nodes' };
  }
  return { status: 'ready', nodes: eligibleNodes };
};

export const useNodesEligibilityCheck = ({
  clusterId,
  enabled,
  optimizationGoal,
}: {
  clusterId: string;
  enabled: boolean;
  optimizationGoal?: OptimizationGoal;
}): NodesEligibilityCheckResult => {
  const isKentRebalancingMode = useIsKentRebalancingMode();

  const rebalancingConfigurationNodesQuery =
    useListAllRebalancingConfigurationNodesQuery({
      params: { clusterId },
      options: { enabled: enabled && !isKentRebalancingMode },
    });

  const nodesQuery = useNodesQuery({
    clusterId,
    enabled: enabled && isKentRebalancingMode,
  });

  const constraintsQuery = useGetKentRebalancingConstraintsQuery({
    enabled: enabled && isKentRebalancingMode,
    request: buildKentConstraintsRequest({
      clusterId,
      optimizationGoal,
    }),
  });

  const { refetch } = makeRefetchOnlyFailed(
    [nodesQuery.error, nodesQuery.refetch],
    [constraintsQuery.error, constraintsQuery.refetch],
    [
      rebalancingConfigurationNodesQuery.error,
      rebalancingConfigurationNodesQuery.refetch,
    ]
  );

  return useMemo(() => {
    if (isKentRebalancingMode) {
      const isLoading = nodesQuery.isLoading || constraintsQuery.isLoading;
      const error = nodesQuery.error || constraintsQuery.error;

      return mapStateToEligibilityResult({
        isLoading,
        error,
        retry: refetch,
        eligibleNodes: getKentEligibleNodes({
          nodes: nodesQuery.nodes || [],
          kentRebalancingConstraints: constraintsQuery.data,
        }),
      });
    }

    const autoscalerGenerateRebalancingPresetPlanParams = optimizationGoal
      ? OPTIMIZATION_GOAL_TO_GENERATE_AUTOSCALER_REBALANCER_PLAN_PARAMS[
          optimizationGoal
        ]
      : undefined;
    const isAggressiveMode =
      autoscalerGenerateRebalancingPresetPlanParams?.aggressiveMode === true;

    return mapStateToEligibilityResult({
      isLoading: rebalancingConfigurationNodesQuery.isLoading,
      error: rebalancingConfigurationNodesQuery.error,
      retry: refetch,
      eligibleNodes: getAutoscalerEligibleNodes({
        rebalancingConfigurationNodes:
          rebalancingConfigurationNodesQuery.data || [],
        aggressiveMode: isAggressiveMode,
        aggressiveModeConfig:
          autoscalerGenerateRebalancingPresetPlanParams?.aggressiveModeConfig,
      }),
    });
  }, [
    isKentRebalancingMode,
    nodesQuery.isLoading,
    nodesQuery.error,
    nodesQuery.nodes,
    constraintsQuery.isLoading,
    constraintsQuery.error,
    constraintsQuery.data,
    rebalancingConfigurationNodesQuery.isLoading,
    rebalancingConfigurationNodesQuery.error,
    rebalancingConfigurationNodesQuery.data,
    optimizationGoal,
    refetch,
  ]);
};
